import { useStoreApi } from '@givebacks/api/store';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { storeKeys } from '../../query-key-factory';
import { useCampaignInfoByProvider } from './utils/campaignUtils';
import { createCauseQueryString } from './utils/createQueryString';

const useDeleteCampaign = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const { causeId } = useCampaignInfoByProvider();
  const causeQuery = createCauseQueryString(causeId);
  const deleteCampaign = async (uuid: string) => {
    const { data } = await StoreApi.delete(`store_items/campaigns/${uuid}?${causeQuery}`);
    return data;
  };
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      if (!causeId) return;
      // Need to invalidate the campaigns and fundraisers query
      queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.campaigns.all,
      });
      queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.fundraisers.all,
      });
    },
  });
};
const useDeleteCampaignImage = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const { causeId, campaignId } = useCampaignInfoByProvider();
  const causeQuery = createCauseQueryString(causeId);

  const deleteCampaignImage = async (uuid: string) => {
    const { data } = await StoreApi.delete(
      `store_items/${campaignId}/images/${uuid}?${causeQuery}`
    );
    return data;
  };
  return useMutation({
    mutationFn: deleteCampaignImage,
    onSuccess: (_, uuid) => {
      if (!causeId) return;
      return queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.campaigns.id(uuid).detail,
      });
    },
  });
};

const useDeleteCampaignThumbnail = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const { causeId, campaignId } = useCampaignInfoByProvider();
  const causeQuery = createCauseQueryString(causeId);

  const deleteCampaignThumbnail = async () => {
    const { data } = await StoreApi.delete(
      `store_items/fundraisers/${campaignId}/thumbnail?${causeQuery}`
    );
    return data;
  };
  return useMutation({
    mutationFn: deleteCampaignThumbnail,
    onSuccess: () => {
      if (!causeId || !campaignId) return;
      return queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.campaigns.id(campaignId).detail,
      });
    },
  });
};

export { useDeleteCampaign, useDeleteCampaignImage, useDeleteCampaignThumbnail };
